import { useQuery } from '@tanstack/react-query'
import { Outlet } from 'react-router'
import { Button } from '@/components/ui/button'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle
} from '@/components/ui/empty'
import { Lock03Icon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { logout } from '@/features/auth/store/actions.ts'
import { operatorQueryOptions } from '@/features/marquee/query-options'
import { useTranslation } from '@/translations/i18n'

/**
 * Resolves the Marquee operator once for the whole section, and decides what
 * happens when Marquee will not answer for whoever just signed in.
 *
 * The sibling of `LuminateSessionRoute`, and the reason that one is a route:
 * **the login is shared, the identity is not**. Anyone with a Luminate account
 * can sign in and reach this URL, and it is `GET /v3/marquee/auth/me` that says
 * whether Marquee knows them at all. So the answer is asked here, once, before
 * any screen below is mounted:
 *
 * - **it answers**: the operator is cached for the session, and every screen
 *   below can take it as given, so `isMarketManagerAllowed` never has to make
 *   sense of a missing answer.
 * - **it fails**: not a Marquee operator, full stop. Without this they would
 *   land on a roster where every read fails one toast at a time. The only way
 *   out is signing out, since the account itself is the problem, and the way in
 *   is someone adding them to the allowlist. It is the interim answer until
 *   Marquee owns its own login (`POST /marquee/auth/login`, story 6.4), at
 *   which point the refusal moves to the sign-in and this branch can go.
 *
 * Every failure counts, the 404 included: a backend with no `/auth/me` cannot
 * say who is working, so it does not let them work. That closes Marquee on any
 * environment where the role partition is not deployed, production included
 * until it ships, which is the deliberate cost of not guessing.
 *
 * A 401 never reaches here either: the SDK's interceptor clears the session on
 * it, and `MarqueeRoute` sends them to the login.
 */
function MarqueeSessionRoute() {
	const { t } = useTranslation()
	const { isPending, isError } = useQuery(operatorQueryOptions())

	if (isPending) {
		return (
			<div className='flex h-full items-center justify-center'>
				<Spinner size={32} />
			</div>
		)
	}

	if (isError) {
		return (
			<Empty className='h-full'>
				<EmptyHeader>
					<EmptyMedia variant='icon'>
						<Lock03Icon />
					</EmptyMedia>
					<EmptyTitle>{t('marquee:unauthorized_title')}</EmptyTitle>
					<EmptyDescription>
						{t('marquee:unauthorized_description')}
					</EmptyDescription>
				</EmptyHeader>
				<Button onClick={logout} size='sm' variant='secondaryOutline'>
					{t('common:logout')}
				</Button>
			</Empty>
		)
	}

	return <Outlet />
}

export { MarqueeSessionRoute }
